import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { inject, observer } from "mobx-react";

import strings from "../../lang/strings";
import {
  ClassMenu,
  ClassExtra,
  CorpMenu,
  CorpExtra,
  PaymentRoutes,
} from "@/config/page_route";

const Content = ({ rootStore }) => {
  const location = useLocation();
  const isCorpMode = rootStore.getCorpMode;

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith("/corp")) {
      rootStore.setCorpMode(true);
    } else if (currentPath.startsWith("/class")) {
      rootStore.setCorpMode(false);
    }
  }, [location.pathname]);

  // 타이틀 설정 로직
  const setTitle = (pathname: string, isCorpMode: boolean) => {
    const defaultTitle = "카리페이 결제";

    // 기관 모드와 클래스 모드에 따라 적절한 메뉴 선택
    const mainMenus = isCorpMode ? CorpMenu : ClassMenu;
    const extraMenus = isCorpMode ? CorpExtra : ClassExtra;

    // 모든 메뉴 아이템을 하나의 배열로 합침
    const allMenuItems = [
      ...extraMenus,
      ...mainMenus.flatMap((menu) => menu.children),
    ];

    // 현재 경로와 일치하는 메뉴 아이템 찾기
    // 더 구체적인 경로가 우선적으로 매칭되도록 정렬
    const sortedMenuItems = [...allMenuItems].sort((a, b) => {
      // 정확한 경로 일치가 우선
      if (pathname === a.path && pathname !== b.path) return -1;
      if (pathname === b.path && pathname !== a.path) return 1;

      // containPath 길이가 긴 것이 우선 (더 구체적인 경로)
      const aContainPathLength = a.containPath
        ? Math.max(...a.containPath.map((p) => p.length))
        : 0;
      const bContainPathLength = b.containPath
        ? Math.max(...b.containPath.map((p) => p.length))
        : 0;

      return bContainPathLength - aContainPathLength;
    });

    const matched = sortedMenuItems.find((menu) => {
      // 정확한 경로 일치 확인
      const matchByPath = pathname === menu.path;

      // containPath 배열이 있는 경우, 해당 경로로 시작하는지 확인
      const matchByContain = menu.containPath?.some((p) =>
        pathname.startsWith(p)
      );

      return matchByPath || matchByContain;
    });

    // 페이지 타이틀 설정
    const pageTitle = matched?.title || defaultTitle;
    const fullTitle = `${pageTitle}`;
    document.title = fullTitle;
  };

  // 페이지 이동 감지 시 타이틀 설정
  useEffect(() => {
    setTitle(location.pathname, rootStore.getCorpMode);
  }, [location.pathname, rootStore.getCorpMode]);

  // 라우트 렌더링 (기존 그대로)
  return (
    <Routes>
      {/* Payment Routes */}
      {PaymentRoutes.map((route, index) => (
        <Route
          key={`payment-${index}`}
          path={route.path}
          element={
            !route.private || rootStore.isSignedIn ? (
              <route.component />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      ))}

      {/* Corp Routes */}
      {CorpMenu.filter(
        (route) =>
          !route.user_type || route.user_type === rootStore.getProfile?.userType
      ).map((route, index) =>
        route.children
          .filter(
            (subRoute) =>
              !subRoute.user_type ||
              subRoute.user_type === rootStore.getProfile?.userType
          )
          .map((subRoute, subIndex) => (
            <Route
              key={`corp-${index}-${subIndex}`}
              path={subRoute.path}
              element={
                !subRoute.private || rootStore.isSignedIn ? (
                  <subRoute.component />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          ))
      )}
      {CorpExtra.filter(
        (route) =>
          !route.user_type || route.user_type === rootStore.getProfile?.userType
      ).map((route, index) => (
        <Route
          key={`corp-extra-${index}`}
          path={route.path}
          element={
            !route.private || rootStore.isSignedIn ? (
              <route.component />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      ))}

      {/* Class Routes */}
      {ClassMenu.filter(
        (route) =>
          !route.user_type || route.user_type === rootStore.getProfile?.userType
      ).map((route, index) =>
        route.children
          .filter(
            (subRoute) =>
              !subRoute.user_type ||
              subRoute.user_type === rootStore.getProfile?.userType
          )
          .map((subRoute, subIndex) => (
            <Route
              key={`class-${index}-${subIndex}`}
              path={subRoute.path}
              element={
                !subRoute.private || rootStore.isSignedIn ? (
                  <subRoute.component />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
          ))
      )}
      {ClassExtra.filter(
        (route) =>
          !route.user_type || route.user_type === rootStore.getProfile?.userType
      ).map((route, index) => (
        <Route
          key={`class-extra-${index}`}
          path={route.path}
          element={
            !route.private || rootStore.isSignedIn ? (
              <route.component />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      ))}
      {/* 카카오 알림톡 템플릿(카리면접 UI_7993/7994)은 /payment?txId= 로 승인돼 있다.
          단수 경로는 쿼리(txId·accessToken)를 그대로 들고 /payments 로 보낸다 —
          아래 catch-all 은 쿼리를 버려 '결제요청 정보가 없습니다'(4003)가 났다. */}
      <Route
        path="/payment"
        element={<Navigate to={{ pathname: "/payments", search: window.location.search }} replace />}
      />
      <Route path="*" element={<Navigate to="/payments" />} />
    </Routes>
  );
};

export default inject("rootStore")(observer(Content));
